import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Star, Quote, X, Send, PenLine } from "lucide-react";
import { useI18n } from "../i18n/LanguageContext";
import { useReveal } from "../hooks/useReveal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Country code → flag emoji (unicode regional indicator symbols)
const flagFor = (code) => {
    if (!code || code.length !== 2) return "🌍";
    return code
        .toUpperCase()
        .split("")
        .map((c) => String.fromCodePoint(127397 + c.charCodeAt(0)))
        .join("");
};

const StarRow = ({ value, size = 14, onChange, testIdPrefix }) => {
    const [hover, setHover] = useState(0);
    return (
        <div
            className="flex items-center gap-0.5"
            onMouseLeave={() => setHover(0)}
        >
            {[1, 2, 3, 4, 5].map((n) => {
                const active = (hover || value) >= n;
                const Comp = onChange ? "button" : "span";
                return (
                    <Comp
                        key={n}
                        type={onChange ? "button" : undefined}
                        onMouseEnter={onChange ? () => setHover(n) : undefined}
                        onClick={onChange ? () => onChange(n) : undefined}
                        data-testid={testIdPrefix ? `${testIdPrefix}-${n}` : undefined}
                        className={`transition-colors ${
                            onChange ? "cursor-pointer p-0.5" : "cursor-default"
                        }`}
                        aria-label={onChange ? `${n} star${n > 1 ? "s" : ""}` : undefined}
                    >
                        <Star
                            width={size}
                            height={size}
                            className={active ? "text-brand-gold fill-brand-gold" : "text-neutral-300"}
                        />
                    </Comp>
                );
            })}
        </div>
    );
};

const formatDate = (iso, lang) => {
    try {
        const d = new Date(iso);
        return d.toLocaleDateString(lang === "pt" ? "pt-PT" : lang === "es" ? "es-ES" : "en-US", {
            year: "numeric",
            month: "short",
            day: "numeric",
        });
    } catch {
        return "";
    }
};

const ReviewCard = ({ review, index, lang }) => {
    const ref = useReveal();
    return (
        <article
            ref={ref}
            data-testid={`review-card-${index}`}
            className="reveal group relative bg-white border border-neutral-200 p-7 md:p-8 hover:border-brand-gold hover:shadow-[0_20px_45px_-20px_rgba(11,27,61,0.25)] transition-all"
            style={{ transitionDelay: `${(index % 3) * 60}ms` }}
        >
            <Quote className="absolute top-6 right-6 w-6 h-6 text-brand-gold/40" />
            <StarRow value={review.rating} size={16} />
            <p className="mt-5 font-serif text-brand-navy text-lg md:text-xl leading-snug italic">
                “{review.message}”
            </p>

            <div className="mt-6 pt-5 border-t border-neutral-200 flex items-center justify-between gap-4">
                <div>
                    <p className="font-sans text-sm text-brand-navy font-medium">
                        {review.name}
                    </p>
                    <p className="font-sans text-xs text-neutral-500 flex items-center gap-2 mt-0.5">
                        <span className="text-base leading-none">{flagFor(review.country_code)}</span>
                        <span>{review.country}</span>
                    </p>
                </div>
                <div className="text-right">
                    {review.tour && (
                        <p className="font-sans text-[10px] uppercase tracking-[0.3em] text-brand-gold mb-1">
                            {review.tour}
                        </p>
                    )}
                    <p className="font-sans text-[10px] text-neutral-400">
                        {formatDate(review.created_at, lang)}
                    </p>
                </div>
            </div>
        </article>
    );
};

const ReviewFormModal = ({ open, onClose, onSubmitted }) => {
    const { t, lang } = useI18n();
    const [form, setForm] = useState({
        name: "",
        country: "",
        country_code: "",
        rating: 0,
        message: "",
        tour: "",
    });
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        if (open) {
            setForm({
                name: "",
                country: "",
                country_code: "",
                rating: 0,
                message: "",
                tour: "",
            });
            setError("");
        }
        if (!open) return;
        const onKey = (e) => e.key === "Escape" && onClose();
        document.body.style.overflow = "hidden";
        window.addEventListener("keydown", onKey);
        return () => {
            document.body.style.overflow = "";
            window.removeEventListener("keydown", onKey);
        };
    }, [open, onClose]);

    if (!open) return null;

    const onChange = (e) =>
        setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

    const onSubmit = async (e) => {
        e.preventDefault();
        setError("");
        if (form.rating < 1) {
            setError(t.reviews.select_rating);
            return;
        }
        setSubmitting(true);
        try {
            const payload = {
                name: form.name.trim(),
                country: form.country.trim(),
                country_code: (form.country_code || "").trim().toUpperCase().slice(0, 2),
                rating: Number(form.rating),
                message: form.message.trim(),
                tour: form.tour || null,
            };
            const { data } = await axios.post(`${API}/reviews`, payload);
            onSubmitted(data);
            onClose();
        } catch (err) {
            setError(t.reviews.error);
        } finally {
            setSubmitting(false);
        }
    };

    const tourOptions = t.tours.items.map((it) => it.name);

    return (
        <div
            data-testid="review-form-modal"
            className="fixed inset-0 z-[80] flex items-center justify-center animate-fade-in p-4"
            role="dialog"
            aria-modal="true"
        >
            <div
                className="absolute inset-0 bg-brand-navy-deep/90 backdrop-blur-md"
                onClick={onClose}
                data-testid="review-modal-backdrop"
            />
            <form
                onSubmit={onSubmit}
                className="relative z-10 w-full max-w-2xl max-h-[92vh] overflow-y-auto bg-white shadow-2xl"
                data-testid="review-form"
            >
                <div className="sticky top-0 flex items-center justify-between p-5 md:p-6 border-b border-neutral-200 bg-white">
                    <div>
                        <p className="font-sans text-[10px] uppercase tracking-[0.4em] text-brand-gold mb-1">
                            {t.reviews.eyebrow}
                        </p>
                        <h3 className="font-serif text-brand-navy text-2xl md:text-3xl leading-tight">
                            {t.reviews.share}
                        </h3>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        data-testid="review-modal-close"
                        aria-label={t.reviews.close}
                        className="p-2 border border-neutral-300 hover:border-brand-navy hover:bg-brand-navy hover:text-white transition-colors"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="p-6 md:p-8 space-y-6">
                    <p className="font-sans text-sm text-neutral-600">
                        {t.reviews.form_hint}
                    </p>

                    <div className="grid md:grid-cols-2 gap-6">
                        <div>
                            <label className="font-sans text-[10px] uppercase tracking-[0.35em] text-neutral-500">
                                {t.reviews.name_label} *
                            </label>
                            <input
                                type="text"
                                name="name"
                                required
                                minLength={2}
                                maxLength={80}
                                value={form.name}
                                onChange={onChange}
                                data-testid="review-input-name"
                                className="mt-2 w-full border-b border-neutral-300 bg-transparent py-3 focus:outline-none focus:border-brand-gold transition-colors font-sans text-brand-navy"
                            />
                        </div>
                        <div>
                            <label className="font-sans text-[10px] uppercase tracking-[0.35em] text-neutral-500">
                                {t.reviews.country_label} *
                            </label>
                            <input
                                type="text"
                                name="country"
                                required
                                minLength={2}
                                maxLength={60}
                                value={form.country}
                                onChange={onChange}
                                placeholder="United States, España, Portugal…"
                                data-testid="review-input-country"
                                className="mt-2 w-full border-b border-neutral-300 bg-transparent py-3 focus:outline-none focus:border-brand-gold transition-colors font-sans text-brand-navy"
                            />
                        </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                        <div>
                            <label className="font-sans text-[10px] uppercase tracking-[0.35em] text-neutral-500">
                                {t.reviews.country_label} (ISO)
                            </label>
                            <input
                                type="text"
                                name="country_code"
                                maxLength={2}
                                value={form.country_code}
                                onChange={onChange}
                                placeholder="US, GB, ES…"
                                data-testid="review-input-country-code"
                                className="mt-2 w-full border-b border-neutral-300 bg-transparent py-3 focus:outline-none focus:border-brand-gold transition-colors font-sans text-brand-navy uppercase"
                            />
                        </div>
                        <div>
                            <label className="font-sans text-[10px] uppercase tracking-[0.35em] text-neutral-500">
                                {t.reviews.tour_label}
                            </label>
                            <select
                                name="tour"
                                value={form.tour}
                                onChange={onChange}
                                data-testid="review-input-tour"
                                className="mt-2 w-full border-b border-neutral-300 bg-transparent py-3 focus:outline-none focus:border-brand-gold transition-colors font-sans text-brand-navy"
                            >
                                <option value="">{t.reviews.tour_placeholder}</option>
                                {tourOptions.map((name) => (
                                    <option key={name} value={name}>
                                        {name}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>

                    <div>
                        <label className="font-sans text-[10px] uppercase tracking-[0.35em] text-neutral-500 block mb-2">
                            {t.reviews.rating_label} *
                        </label>
                        <StarRow
                            value={form.rating}
                            size={28}
                            onChange={(v) => setForm((f) => ({ ...f, rating: v }))}
                            testIdPrefix="review-rating-star"
                        />
                    </div>

                    <div>
                        <label className="font-sans text-[10px] uppercase tracking-[0.35em] text-neutral-500">
                            {t.reviews.message_label} *
                        </label>
                        <textarea
                            name="message"
                            required
                            minLength={10}
                            maxLength={1000}
                            rows={5}
                            value={form.message}
                            onChange={onChange}
                            data-testid="review-input-message"
                            className="mt-2 w-full border border-neutral-300 bg-transparent p-3 focus:outline-none focus:border-brand-gold transition-colors font-sans text-brand-navy resize-none"
                        />
                        <p className="text-right text-xs text-neutral-400 mt-1">
                            {form.message.length} / 1000
                        </p>
                    </div>

                    {error && (
                        <p
                            data-testid="review-form-error"
                            className="font-sans text-sm text-red-600 border border-red-200 bg-red-50 p-3"
                        >
                            {error}
                        </p>
                    )}

                    <button
                        type="submit"
                        disabled={submitting}
                        data-testid="review-form-submit"
                        className="w-full flex items-center justify-center gap-3 bg-brand-navy hover:bg-brand-navy-hover disabled:bg-neutral-400 text-white py-4 font-sans text-xs uppercase tracking-[0.3em] transition-colors"
                    >
                        <Send className="w-4 h-4" />
                        <span>{submitting ? t.reviews.submitting : t.reviews.submit}</span>
                    </button>
                </div>
            </form>
        </div>
    );
};

const Testimonials = () => {
    const { t, lang } = useI18n();
    const [reviews, setReviews] = useState([]);
    const [total, setTotal] = useState(0);
    const [average, setAverage] = useState(0);
    const [loading, setLoading] = useState(true);
    const [openForm, setOpenForm] = useState(false);
    const [successMsg, setSuccessMsg] = useState("");
    const [visible, setVisible] = useState(6);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await axios.get(`${API}/reviews`, { params: { limit: 50 } });
            setReviews(data.items || []);
            setTotal(data.total || 0);
            setAverage(data.average || 0);
        } catch (e) {
            // silent
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, []);

    useEffect(() => {
        if (!successMsg) return;
        const t = setTimeout(() => setSuccessMsg(""), 5000);
        return () => clearTimeout(t);
    }, [successMsg]);

    const onSubmitted = (created) => {
        setReviews((prev) => [created, ...prev]);
        setTotal((n) => n + 1);
        setSuccessMsg(t.reviews.success);
        // Refresh average from server (fire and forget)
        load();
    };

    const shown = useMemo(() => reviews.slice(0, visible), [reviews, visible]);

    return (
        <section
            id="reviews"
            data-testid="reviews-section"
            className="bg-brand-ivory py-24 md:py-32 relative overflow-hidden"
        >
            <div className="absolute top-0 left-1/2 -translate-x-1/2 h-40 w-px bg-brand-gold/40" />

            <div className="max-w-7xl mx-auto px-6 md:px-10">
                <div className="text-center mb-14 md:mb-16 max-w-3xl mx-auto">
                    <p className="font-sans text-brand-gold text-xs uppercase tracking-[0.5em] mb-5">
                        — {t.reviews.eyebrow}
                    </p>
                    <h2
                        data-testid="reviews-title"
                        className="font-serif text-brand-navy text-4xl md:text-5xl lg:text-6xl leading-tight"
                    >
                        {t.reviews.title}
                    </h2>
                    <p className="mt-6 font-sans text-neutral-600">{t.reviews.intro}</p>

                    {total > 0 && (
                        <div className="mt-8 inline-flex items-center gap-4 border border-brand-gold/40 bg-white px-6 py-4">
                            <div className="flex items-center gap-1">
                                <StarRow value={Math.round(average)} size={18} />
                            </div>
                            <div className="text-left leading-tight">
                                <div className="font-serif text-brand-navy text-2xl">
                                    {average.toFixed(1)}
                                    <span className="text-brand-gold text-sm ml-1">/ 5</span>
                                </div>
                                <div className="font-sans text-[10px] uppercase tracking-[0.3em] text-neutral-500">
                                    {t.reviews.based_on} {total} {t.reviews.total_reviews}
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                <div className="flex justify-center mb-10">
                    <button
                        onClick={() => setOpenForm(true)}
                        data-testid="open-review-form"
                        className="inline-flex items-center gap-3 bg-brand-gold hover:bg-brand-gold-hover text-white px-8 py-4 font-sans text-xs uppercase tracking-[0.28em] transition-all hover:shadow-[0_20px_40px_rgba(212,175,55,0.35)]"
                    >
                        <PenLine className="w-4 h-4" />
                        <span>{t.reviews.leave}</span>
                    </button>
                </div>

                {successMsg && (
                    <div
                        data-testid="review-success-banner"
                        className="max-w-2xl mx-auto mb-8 border border-green-300 bg-green-50 text-green-800 font-sans text-sm px-5 py-3 text-center"
                    >
                        {successMsg}
                    </div>
                )}

                {loading ? (
                    <p
                        data-testid="reviews-loading"
                        className="text-center font-sans text-sm text-neutral-500"
                    >
                        ···
                    </p>
                ) : reviews.length === 0 ? (
                    <p
                        data-testid="reviews-empty"
                        className="text-center font-sans text-neutral-500"
                    >
                        {t.reviews.no_reviews}
                    </p>
                ) : (
                    <>
                        <div
                            data-testid="reviews-grid"
                            className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-7"
                        >
                            {shown.map((r, i) => (
                                <ReviewCard key={r.id} review={r} index={i} lang={lang} />
                            ))}
                        </div>

                        {reviews.length > visible && (
                            <div className="flex justify-center mt-10">
                                <button
                                    onClick={() => setVisible((v) => v + 6)}
                                    data-testid="reviews-load-more"
                                    className="border border-brand-navy hover:bg-brand-navy hover:text-white text-brand-navy px-8 py-3 font-sans text-[11px] uppercase tracking-[0.28em] transition-colors"
                                >
                                    + {reviews.length - visible}
                                </button>
                            </div>
                        )}
                    </>
                )}
            </div>

            <ReviewFormModal
                open={openForm}
                onClose={() => setOpenForm(false)}
                onSubmitted={onSubmitted}
            />
        </section>
    );
};

export default Testimonials;
